import React, { useState, useEffect } from 'react';
import { AlertCircle } from 'lucide-react';
import { userService } from '../../../../services/user.service';

const EditUserModal = ({ user, onClose, onSuccess, editorRole }) => {
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    username: user.username,
    email: user.email,
    role: user.role,
    profile: {
      fullName: user.fullName || '',
      phone: user.phone || '',
      gender: user.profile?.gender || '',
      age: user.profile?.age || '',
      nationality: user.profile?.nationality || '',
      courseTaken: user.profile?.courseTaken || '',
      religion: user.profile?.religion || '',
      yearOfStudy: user.profile?.yearOfStudy || '',
      residence: user.profile?.residence || '',
      maritalStatus: user.profile?.maritalStatus || ''
    }
  });
  const [roleOptions, setRoleOptions] = useState([]);

  useEffect(() => {
    // Restrict roles based on the current user's role
    if (editorRole === 'admin') {
      setRoleOptions(['counselor', 'admin', 'receptionist', 'hod', 'staff']);
    } else if (editorRole === 'receptionist') {
      setRoleOptions(['student']);
    }
  }, [editorRole]);

  const validateForm = () => {
    const errors = [];

    if (!formData.username?.trim()) errors.push('Username is required');
    if (!formData.email?.trim()) errors.push('Email is required');
    if (!formData.role) errors.push('Role is required');
    if (!formData.fullName?.trim()) errors.push('Full name is required');

    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      errors.push('Invalid email format');
    }

    if (errors.length > 0) {
      setError(errors.join(', '));
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    setError(null);

    try {
      // Log the exact response
      const result = await userService.updateUser(user._id, formData);

      if (result && result.status === 'success') {
        onSuccess(user._id, result.data.user);  // Pass both ID and updated data
        onClose();
      } else {
        throw new Error('Invalid server response');
      }
    } catch (err) {
      console.error('Update error:', err); // Add this line with full error
      setError(err?.message || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-medium">Edit User</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            ×
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border-l-4 border-red-400 p-4">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-400" />
              <p className="ml-3 text-sm text-red-700">{error}</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Full Name *
              </label>
              <input
                type="text"
                value={formData.fullName}
                onChange={(e) => handleInputChange('fullName', e.target.value)}
                className="w-full px-3 py-2 border rounded-md focus:ring-uzima-maroon-400 focus:border-uzima-maroon-400"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email *
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className="w-full px-3 py-2 border rounded-md focus:ring-uzima-maroon-400 focus:border-uzima-maroon-400"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Username *
              </label>
              <input
                type="text"
                value={formData.username}
                onChange={(e) => handleInputChange('username', e.target.value)}
                className="w-full px-3 py-2 border rounded-md focus:ring-uzima-maroon-400 focus:border-uzima-maroon-400"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone
              </label>
              <input
                type="tel"
                value={formData.profile.phone}
                onChange={(e) => handleInputChange('profile.phone', e.target.value)}
                className="w-full px-3 py-2 border rounded-md focus:ring-uzima-maroon-400 focus:border-uzima-maroon-400"
              />
            </div>
          </div>

          {/* Additional Information */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Role *
              </label>
              <select
                value={formData.role}
                onChange={(e) => handleInputChange('role', e.target.value)}
                className="w-full px-3 py-2 border rounded-md focus:ring-uzima-maroon-400 focus:border-uzima-maroon-400"
                required
              >
                <option value="student">Student</option>
                <option value="counselor">Counselor</option>
                <option value="receptionist">Receptionist</option>
                <option value="admin">Admin</option>
                <option value="hod">Head of Department</option>
              </select>

            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Gender
              </label>
              <select
                value={formData.profile.gender}
                onChange={(e) => handleInputChange('profile.gender', e.target.value)}
                className="w-full px-3 py-2 border rounded-md focus:ring-uzima-maroon-400 focus:border-uzima-maroon-400"
              >
                <option value="">Select Gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>

            {formData.role === 'student' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Course Taken
                  </label>
                  <input
                    type="text"
                    value={formData.profile.courseTaken}
                    onChange={(e) => handleInputChange('profile.courseTaken', e.target.value)}
                    className="w-full px-3 py-2 border rounded-md focus:ring-uzima-maroon-400 focus:border-uzima-maroon-400"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Year of Study
                  </label>
                  <select
                    value={formData.profile.yearOfStudy}
                    onChange={(e) => handleInputChange('profile.yearOfStudy', e.target.value)}
                    className="w-full px-3 py-2 border rounded-md focus:ring-uzima-maroon-400 focus:border-uzima-maroon-400"
                  >
                    <option value="">Select Year</option>
                    <option value="1">Freshman</option>
                    <option value="2">Sophomore</option>
                    <option value="3">Junior</option>
                    <option value="4">Senior</option>
                    <option value="5">Graduate Student</option>
                  </select>
                </div>
              </>
            )}
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-uzima-maroon-500 text-white rounded-md hover:bg-uzima-maroon-600 disabled:bg-blue-300 flex items-center"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditUserModal;